import React, { useEffect, useRef, useState } from 'react'
import { HiX } from 'react-icons/hi'
import Drawer from '@/components/ui/Drawer'
import { useNoteTags } from '@/api/notes/useNoteTags'
import { useAllTags, invalidateTagsCache } from '@/api/notes/useAllTags'
import useResponsive from '@/utils/hooks/useResponsive'

// ─── Validation ──────────────────────────────────────────────────────────────

const TAG_MAX_LEN = 50
const TAG_REGEX = /^[a-zA-Z0-9\- ]+$/
const TAG_MAX_COUNT = 5

function validateTag(
  name: string,
  currentTags: string[]
): string | null {
  if (!name) return null
  if (name.length > TAG_MAX_LEN) return `Tag may not exceed ${TAG_MAX_LEN} characters.`
  if (!TAG_REGEX.test(name)) return 'Only letters, numbers, hyphens and spaces are allowed.'
  if (currentTags.includes(name)) return 'This tag is already added.'
  if (currentTags.length >= TAG_MAX_COUNT) return `A note may have at most ${TAG_MAX_COUNT} tags.`
  return null
}

// ─── Inner editor UI (used both inline and inside Drawer) ────────────────────

interface EditorBodyProps {
  noteId: string
  tags: string[]
  onClose?: () => void
}

const EditorBody: React.FC<EditorBodyProps> = ({ noteId, tags, onClose }) => {
  const { addTag, removeTag, loading } = useNoteTags(noteId)
  const { tags: allTags } = useAllTags()
  const [input, setInput] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const suggestionsRef = useRef<HTMLDivElement>(null)

  const trimmed = input.trim()
  const suggestions = allTags.filter(
    (t) =>
      t.toLowerCase().includes(trimmed.toLowerCase()) &&
      !tags.includes(t) &&
      trimmed.length > 0
  )

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const commit = async (name: string) => {
    const trimmedName = name.trim()
    const validationError = validateTag(trimmedName, tags)
    if (validationError) {
      setError(validationError)
      return
    }
    setError(null)
    setInput('')
    setShowSuggestions(false)
    try {
      await addTag(trimmedName)
      invalidateTagsCache()
    } catch (e: any) {
      setError(e?.message || 'Failed to add tag.')
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      void commit(trimmed)
    } else if (e.key === 'Escape') {
      setShowSuggestions(false)
      onClose?.()
    }
  }

  const handleRemove = async (tag: string) => {
    setError(null)
    try {
      await removeTag(tag)
      invalidateTagsCache()
    } catch (e: any) {
      setError(e?.message || 'Failed to remove tag.')
    }
  }

  return (
    <div className="flex flex-col gap-2">
      {/* Existing tag chips */}
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300 border border-primary-200 dark:border-primary-700"
            >
              {tag}
              <button
                type="button"
                onClick={() => handleRemove(tag)}
                disabled={loading}
                className="ml-0.5 rounded-full hover:bg-primary-200 dark:hover:bg-primary-800 p-0.5 transition-colors disabled:opacity-50"
                aria-label={`Remove tag ${tag}`}
              >
                <HiX className="w-2.5 h-2.5" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Input + autocomplete */}
      {tags.length < TAG_MAX_COUNT && (
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => {
              setInput(e.target.value)
              setError(null)
              setShowSuggestions(true)
            }}
            onFocus={() => setShowSuggestions(true)}
            onKeyDown={handleKeyDown}
            placeholder="Add tag…"
            disabled={loading}
            className="w-full text-sm px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-400 disabled:opacity-50"
            maxLength={TAG_MAX_LEN}
          />

          {showSuggestions && suggestions.length > 0 && (
            <div
              ref={suggestionsRef}
              className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded shadow-lg max-h-40 overflow-y-auto"
            >
              {suggestions.map((s) => (
                <button
                  key={s}
                  type="button"
                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-primary-50 dark:hover:bg-primary-900/30 text-gray-800 dark:text-gray-200"
                  onMouseDown={(e) => {
                    e.preventDefault()
                    void commit(s)
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Validation error */}
      {error && (
        <p className="text-xs text-red-500 dark:text-red-400">{error}</p>
      )}
    </div>
  )
}

// ─── Public component ─────────────────────────────────────────────────────────

interface NoteTagsEditorProps {
  noteId: string
  tags: string[]
  /** Called when the editor should close (Escape, Drawer close button). */
  onClose?: () => void
}

/**
 * Tag editor with add/remove.
 * - Desktop: renders inline.
 * - Mobile (< md): renders inside a bottom Drawer.
 */
const NoteTagsEditor: React.FC<NoteTagsEditorProps> = ({
  noteId,
  tags,
  onClose,
}) => {
  const { smaller } = useResponsive()
  const isMobile = smaller.md

  if (isMobile) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const drawerProps: any = {
        isOpen: true,
        onClose: () => onClose?.(),
        placement: 'bottom',
        height: 320,
        title: 'Tags',
        closable: true,
      }
      return (
        <Drawer {...drawerProps}>
        <div className="px-1 pt-2">
          <EditorBody noteId={noteId} tags={tags} onClose={onClose} />
        </div>
      </Drawer>
    )
  }

  return (
    <div className="w-full" onClick={(e) => e.stopPropagation()}>
      <EditorBody noteId={noteId} tags={tags} onClose={onClose} />
    </div>
  )
}

export default NoteTagsEditor

